import { usePostLoginTokenMutation } from '@/api/api.slice.tsx';
import { CredentialLoginToken } from '@/api/types.ts';
import { getApplicationState, setIsLogged } from '@/app/Application.slice.ts';
import { useAppDispatch, useAppSelector } from '@/app/hooks.ts';
import { Loader, Logger, toast } from '@arsnovasystems/ars-front-lib';
import { Box } from '@mui/material';
import { Mutex } from 'async-mutex';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';

const mutex = new Mutex();

/**
 * Wrapper component to enforce authentication
 * use the current logged state which will be updated asynchronously
 *
 * @param props           Props
 * @param props.redirect  where to redirect if not logged
 * @param props.children  the children to render if logged
 * @param props.token     the token to use for login
 * @param props.tokenType the token type
 * @param props.graceful  if true, will not display the session expired alert
 *
 * @returns               AuthToken component
 */
export const AuthToken = ({
  redirect = '/login',
  token,
  tokenType,
  graceful,
  children,
}: {
  redirect?: string;
  token: string;
  tokenType: string;
  graceful: boolean;
  children: ReactNode | ReactNode[];
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isLogged } = useAppSelector(getApplicationState);

  const [loginUserWithToken, { isSuccess, isUninitialized, isLoading }] =
    usePostLoginTokenMutation();

  useEffect(() => {
    async function load() {
      if (isLogged || isLoading) return;

      if (mutex.isLocked()) {
        return;
      }

      const release = await mutex.acquire();
      try {
        const credential: CredentialLoginToken = {
          type: tokenType as CredentialLoginToken['type'],
          token: token,
        };

        const res = await loginUserWithToken(credential);
        const error = res.error;
        dispatch(setIsLogged(error === undefined));
      } catch (error) {
        Logger.error('Error while authenticating with token', { error });
        dispatch(setIsLogged(false));
      } finally {
        release();
      }
    }
    void load();
  }, [dispatch, isLoading, isLogged, loginUserWithToken, token, tokenType]);

  useEffect(() => {
    //app state does not match the server state
    if (isSuccess && !isLogged) {
      dispatch(setIsLogged(true));
    }
  }, [isSuccess, isLogged, dispatch]);

  if (isLogged || isSuccess) {
    return <>{children}</>;
  }

  if (isUninitialized || isLoading) {
    return (
      <Box className='not-logged'>
        <Loader />
      </Box>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!isUninitialized && !isSuccess && !isLogged && !isLoading) {
    if (!graceful) {
      toast({
        title: t('ik.lib.messageDispatcher.alert.title.sessionExpired'),
        description: t(
          'ik.lib.messageDispatcher.alert.description.sessionExpired',
        ),
      });
    }

    return <Navigate to={redirect} />;
  }

  toast({
    title: t('ik.lib.messageDispatcher.alert.title.sessionExpired'),
    description: t('ik.lib.messageDispatcher.alert.description.sessionExpired'),
  });
  return <Navigate to={redirect} />;
};
